import type {
  CredencialDoCatalogo,
  EtapaDoProvisionamento,
  PedidoDeProvisionamento,
  Problema,
  ProvisionamentoDoConvenio,
} from "@/api/contrato";
import { FalhaDaApi } from "@/api/problema";
import {
  errosDoFormulario,
  rotuloDoCampo,
  textoDaRecusa,
  textoDoLimiteNoProvisionamento,
  textoDoMotivo,
  type ErrosDoFormulario,
} from "./recusas";

/*
 * O provisionamento na tela: o estado, os campos do catálogo e as recusas com `etapa`.
 *
 * Provisionar é reentrante: repetir completa o que falta, e nunca duplica. Por isso a recusa
 * do provider, aqui, sempre pode dizer que dá para repetir, ao contrário do certificado, em que
 * o envio pode ter chegado e a saída é conferir antes.
 */

export type ValoresDaCredencial = Partial<Record<CredencialDoCatalogo, string>>;

/** `API - ID` é o nome da GUI deles, que é o que o cliente lê no portal (design D11). */
export const ROTULO_DA_CREDENCIAL: Record<CredencialDoCatalogo, string> = {
  api_id: "API - ID",
  api_key: "API - Key",
  api_secret: "API - Secret",
  estacao: "Estação",
};

/**
 * A credencial vai no corpo, e só o que foi preenchido: quem já existe lá não reenvia nada, e
 * a API responde `credencial_obrigatoria` se o convênio precisar ser criado.
 */
export function corpoDoProvisionamento(valores: ValoresDaCredencial): PedidoDeProvisionamento {
  const pedido: PedidoDeProvisionamento = { confirmado: true };

  for (const [credencial, valor] of Object.entries(valores)) {
    if (valor.trim() !== "") pedido[credencial as CredencialDoCatalogo] = valor.trim();
  }
  return pedido;
}

const NOME_DA_ETAPA: Record<EtapaDoProvisionamento, string> = {
  cedente: "o cedente",
  conta: "a conta",
  convenio: "o convênio",
  certificado: "o certificado",
};

/** `etapa` é extensão das recusas do provisionamento, e não está em `Problema`. */
function etapaDe(falha: FalhaDaApi): EtapaDoProvisionamento | undefined {
  const problema: (Problema & { etapa?: EtapaDoProvisionamento }) | null = falha.problema;
  return problema?.etapa;
}

/** O que a TecnoSpeed recusou, pela etapa: `a conta`, e `o cadastro` sem ela. */
function alvoDaRecusa(falha: FalhaDaApi): string {
  const etapa = etapaDe(falha);
  return etapa === undefined ? "o cadastro" : NOME_DA_ETAPA[etapa];
}

const REPETIR_REAPROVEITA = "O que já foi criado é reaproveitado.";

/** A recusa em palavras de tela, com a etapa e o campo que a resposta trouxe. */
export function textoDaRecusaDoProvisionamento(falha: unknown): string {
  if (!(falha instanceof FalhaDaApi)) return textoDaRecusa(falha);

  const base = textoDaRecusa(falha);
  const campo = falha.problema?.campo;

  switch (falha.codigo) {
    case "provisionamento_recusado":
      return campo === undefined
        ? `A TecnoSpeed recusou ${alvoDaRecusa(falha)}.`
        : `A TecnoSpeed recusou ${alvoDaRecusa(falha)}. Confira: ${rotuloDoCampo(campo)}.`;
    case "dado_divergente_no_provider":
      return campo === undefined
        ? base
        : `A TecnoSpeed tem outro valor em ${rotuloDoCampo(campo)}. A correção lá é manual.`;
    case "provider_indisponivel":
      return falha.tenteEmSegundos === null ? base : `${base} ${REPETIR_REAPROVEITA}`;
    case "provider_resposta_invalida":
      return `${base} ${REPETIR_REAPROVEITA}`;
    case "provider_sem_orcamento":
      // "Nada foi enviado" é a frase do envio avulso: aqui, as etapas de antes já estão lá.
      return `${textoDoLimiteNoProvisionamento(falha)} ${REPETIR_REAPROVEITA}`;
    default:
      return base;
  }
}

/** A recusa no campo da credencial, quando ela aponta um, ou na faixa do modal. */
export function errosDoProvisionamento(
  falha: unknown,
  credenciais: readonly CredencialDoCatalogo[],
): ErrosDoFormulario<CredencialDoCatalogo> {
  const { porCampo, geral } = errosDoFormulario(falha, credenciais);
  return { porCampo, geral: geral === undefined ? undefined : textoDaRecusaDoProvisionamento(falha) };
}

const ROTULO_DO_ESTADO: Record<ProvisionamentoDoConvenio["estado"], string> = {
  nao_provisionado: "não provisionado",
  provisionado: "provisionado",
  falhou: "falhou",
};

export function rotuloDoEstado(estado: ProvisionamentoDoConvenio["estado"]): string {
  return ROTULO_DO_ESTADO[estado];
}

/** O motivo gravado na última tentativa, ou o fato de nada ter ido lá ainda. */
export function dicaDoEstado({ estado, motivo, campo }: ProvisionamentoDoConvenio): string {
  return estado === "falhou"
    ? textoDoMotivo(motivo, campo)
    : "Este convênio ainda não está na TecnoSpeed.";
}

/** Por que a pílula `dados alterados` está ali, e o que mudou. Só nomes: nunca o valor. */
export function dicaDosDadosAlterados(campos: readonly string[]): string {
  const nomes = [...new Set(campos.map(rotuloDoCampo))].join(", ");
  return `Mudou depois de provisionar: ${nomes}. A alteração não foi à TecnoSpeed, e a correção lá é manual.`;
}
